import type { AdminMemberAccount, AdminOrganizationTreeNode } from './api'

export interface DepartmentOption {
  depth: number
  id: string
  isDefaultDepartment: boolean
  label: string
  memberCount: number
  name: string
}

export interface OrganizationMemberNode {
  email: string
  id: string
  name: string
  role?: string
  source?: string
  status?: 'active' | 'banned'
}

export interface OrganizationDepartmentNode {
  children: OrganizationDepartmentNode[]
  code?: string | null
  depth: number
  id: string
  isDefaultDepartment: boolean
  memberCount: number
  members: OrganizationMemberNode[]
  name: string
  parentId?: string | null
  source?: string
  type: string
}

export function normalizeDepartmentTree(
  node: AdminOrganizationTreeNode,
  depth = 0,
  parentId: string | null = null,
): OrganizationDepartmentNode {
  const members: OrganizationMemberNode[] = []
  const children: OrganizationDepartmentNode[] = []

  for (const child of node.children ?? []) {
    if (child.type === 'member') {
      members.push({
        email: child.email ?? '',
        id: child.id,
        name: child.name,
        role: child.role,
        source: child.source,
        status: child.status,
      })
      continue
    }

    children.push(normalizeDepartmentTree(child, depth + 1, node.id))
  }

  return {
    children,
    code: node.code,
    depth,
    id: node.id,
    isDefaultDepartment: Boolean(node.is_default_department),
    memberCount: node.member_count ?? members.length,
    members,
    name: node.name,
    parentId,
    source: node.source,
    type: node.type,
  }
}

export function flattenDepartments(root?: OrganizationDepartmentNode | null): DepartmentOption[] {
  if (!root) {
    return []
  }

  const items: DepartmentOption[] = []
  const visit = (node: OrganizationDepartmentNode) => {
    items.push({
      depth: node.depth,
      id: node.id,
      isDefaultDepartment: node.isDefaultDepartment,
      label: `${node.name} (${String(node.memberCount)})`,
      memberCount: node.memberCount,
      name: node.name,
    })
    node.children.forEach(visit)
  }

  visit(root)

  return items
}

export function findDepartment(
  root: OrganizationDepartmentNode | null,
  id: string,
): OrganizationDepartmentNode | null {
  if (!root) {
    return null
  }

  if (root.id === id) {
    return root
  }

  for (const child of root.children) {
    const found = findDepartment(child, id)
    if (found) {
      return found
    }
  }

  return null
}

export function memberDepartmentSummary(member: AdminMemberAccount) {
  if (member.departments.length === 0) {
    return '-'
  }

  if (member.departments.length === 1) {
    return member.departments[0]?.name ?? '-'
  }

  const [firstDepartment] = member.departments
  return `${firstDepartment.name} +${String(member.departments.length - 1)}`
}
